// src/utils/useScreenWakeLock.js
import { useEffect, useRef } from 'react';

/**
 * useScreenWakeLock(active)
 *
 * Holds a screen Wake Lock while `active` is true so the OS doesn't
 * dim / sleep / suspend the tab during workout playback.
 *
 * WHY: iOS aggressively evicts backgrounded PWA processes. Once a member's
 * installed-to-homescreen PWA is evicted mid-workout, the Clerk session must
 * re-hydrate from cookie on relaunch — and if that coincides with a network
 * or Clerk-API blip, she comes back signed-out and our route guard
 * (`ClerkProtected` in App.js) bounces her to the landing page mid-stream.
 * Keeping the screen awake keeps the app FOREGROUNDED, which is what prevents
 * the eviction in the first place. This does not (and cannot) stop iOS from
 * evicting a truly backgrounded tab — it removes the most common trigger.
 *
 * Mirrors the proven LiveBroadcaster wake-lock pattern (2026-04-23):
 *   - acquire when active
 *   - browsers auto-release the lock on visibility loss → re-acquire on tab
 *     return so a quick app-switch + return during a workout doesn't drop it
 *   - release when inactive / on unmount
 *
 * Best-effort + graceful degradation: feature-detected and fully wrapped in
 * try/catch. On browsers without the Wake Lock API (older iOS, etc.) it is a
 * complete no-op — exactly today's behavior, just without the preventive
 * screen-awake. Never throws; never touches playback or auth state.
 */
export function useScreenWakeLock(active) {
    const sentinelRef = useRef(null);

    useEffect(() => {
        if (!active) return;
        if (typeof navigator === 'undefined' || !navigator.wakeLock?.request) return;

        // Guards against an effect that tore down while the async
        // `request('screen')` promise was still in flight — without it we
        // could store (and leak) a sentinel the caller already abandoned.
        let cancelled = false;

        const acquire = async () => {
            // Don't double-acquire if the current lock is still live.
            if (sentinelRef.current && !sentinelRef.current.released) return;
            try {
                const sentinel = await navigator.wakeLock.request('screen');
                if (cancelled) {
                    try { sentinel.release?.(); } catch (_) { /* ignore */ }
                    return;
                }
                sentinelRef.current = sentinel;
            } catch (_) {
                // Unsupported / denied (e.g. tab not visible at request time).
                // Best-effort: the visibilitychange handler retries on return.
            }
        };

        // Browsers auto-release screen wake locks when the tab is hidden.
        // Re-acquire when it returns so long workouts stay protected across a
        // brief tab-switch.
        const onVisible = () => {
            if (document.visibilityState === 'visible') acquire();
        };

        acquire();
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            cancelled = true;
            document.removeEventListener('visibilitychange', onVisible);
            if (sentinelRef.current) {
                try { sentinelRef.current.release?.(); } catch (_) { /* ignore */ }
                sentinelRef.current = null;
            }
        };
    }, [active]);
}
